import { useEffect, useState } from "react";
import { VEHICLE_REVIEW_PRESETS, VEHICLE_REVIEW_STATUSES } from "../../../../constants/vehicleSourceConstants";
import {
  applyVehicleSourceReviewPreset,
  patchVehicleSourceReview,
} from "../../../../services/vehicleSourceApi";
import { runVehicleSourceTestIngestion } from "../../../../services/vehicleIngestionApi";

const EMPTY_REVIEW = {
  robotsTxtUrl: "",
  robotsReviewStatus: "NOT_REVIEWED",
  robotsReviewNotes: "",
  termsUrl: "",
  termsReviewStatus: "NOT_REVIEWED",
  termsReviewNotes: "",
  accessReviewStatus: "NOT_REVIEWED",
  accessReviewNotes: "",
  rateLimitNotes: "",
  dataSensitivityNotes: "",
  containsPersonalData: false,
  allowedToIngest: false,
  blockedReason: "",
  complianceNotes: "",
};

function sourceToReviewForm(source) {
  if (!source) return { ...EMPTY_REVIEW };
  return {
    robotsTxtUrl: source.robotsTxtUrl || "",
    robotsReviewStatus: source.robotsReviewStatus || "NOT_REVIEWED",
    robotsReviewNotes: source.robotsReviewNotes || "",
    termsUrl: source.termsUrl || source.baseUrl || "",
    termsReviewStatus: source.termsReviewStatus || "NOT_REVIEWED",
    termsReviewNotes: source.termsReviewNotes || "",
    accessReviewStatus: source.accessReviewStatus || "NOT_REVIEWED",
    accessReviewNotes: source.accessReviewNotes || "",
    rateLimitNotes: source.rateLimitNotes || "",
    dataSensitivityNotes: source.dataSensitivityNotes || "",
    containsPersonalData: Boolean(source.containsPersonalData),
    allowedToIngest: Boolean(source.allowedToIngest),
    blockedReason: source.blockedReason || "",
    complianceNotes: source.complianceNotes || "",
  };
}

const VehicleSourceReviewPanel = ({ source, canManage, onClose, onSaved }) => {
  const [form, setForm] = useState(() => sourceToReviewForm(source));
  const [saving, setSaving] = useState(false);
  const [presetBusy, setPresetBusy] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [blockReasonDraft, setBlockReasonDraft] = useState("");
  const [testRunResult, setTestRunResult] = useState(null);
  const [testRunBusy, setTestRunBusy] = useState(false);

  useEffect(() => {
    setForm(sourceToReviewForm(source));
    setErrorMessage("");
    setBlockReasonDraft(source?.blockedReason || "");
  }, [source]);

  if (!source) return null;

  const handleChange = (field) => (event) => {
    const value =
      field === "containsPersonalData" || field === "allowedToIngest"
        ? event.target.checked
        : event.target.value;
    setForm((current) => ({ ...current, [field]: value }));
  };

  const handleSave = async (event) => {
    event.preventDefault();
    if (!canManage) return;
    setSaving(true);
    setErrorMessage("");
    try {
      const result = await patchVehicleSourceReview(source.id, {
        ...form,
        robotsTxtUrl: form.robotsTxtUrl.trim() || null,
        termsUrl: form.termsUrl.trim() || null,
        blockedReason: form.blockedReason.trim() || null,
      });
      onSaved(result.source, result.ingestReadiness);
      onClose();
    } catch (error) {
      setErrorMessage(error.message || "Unable to save review.");
    } finally {
      setSaving(false);
    }
  };

  const runPreset = async (preset) => {
    if (!canManage) return;
    if (preset === "block" && !blockReasonDraft.trim()) {
      setErrorMessage("Enter a blocked reason before blocking this source.");
      return;
    }
    setPresetBusy(preset);
    setErrorMessage("");
    try {
      const result = await applyVehicleSourceReviewPreset(source.id, preset, {
        blockedReason: preset === "block" ? blockReasonDraft.trim() : undefined,
      });
      onSaved(result.source, result.ingestReadiness);
      onClose();
    } catch (error) {
      setErrorMessage(error.message || "Unable to apply review action.");
    } finally {
      setPresetBusy("");
    }
  };

  const ingestReady = source.ingestReadiness?.allowed;

  return (
    <div className="admin-modal-backdrop" role="presentation" onClick={onClose}>
      <div
        className="admin-modal admin-modal--wide"
        role="dialog"
        aria-labelledby="vehicle-source-review-title"
        onClick={(event) => event.stopPropagation()}
      >
        <header className="admin-modal__header">
          <div>
            <h2 id="vehicle-source-review-title">Source review — {source.sourceName}</h2>
            <p className="muted admin-vehicle-sources-dash__key">{source.sourceKey}</p>
          </div>
          <button type="button" className="btn btn--ghost" onClick={onClose}>
            Close
          </button>
        </header>

        <div className="admin-modal__body">
          <p
            className={`admin-vehicle-review-readiness ${
              ingestReady ? "admin-vehicle-review-readiness--ok" : "admin-vehicle-review-readiness--blocked"
            }`}
            role="status"
          >
            {ingestReady
              ? "Ready for future ingestion (all compliance gates pass)."
              : `Not ready for ingestion${
                  source.ingestReadiness?.reasons?.length
                    ? `: ${source.ingestReadiness.reasons[0]}`
                    : "."
                }`}
          </p>

          {canManage ? (
            <div className="admin-vehicle-review-presets">
              {VEHICLE_REVIEW_PRESETS.map((preset) => (
                <button
                  key={preset.value}
                  type="button"
                  className="btn btn--ghost btn--sm"
                  disabled={Boolean(presetBusy)}
                  onClick={() => runPreset(preset.value)}
                >
                  {presetBusy === preset.value ? "Applying…" : preset.label}
                </button>
              ))}
            </div>
          ) : null}

          {presetBusy === "block" || form.blockedReason ? (
            <label className="admin-form-grid__full">
              Blocked reason (required to block)
              <textarea
                rows={2}
                value={blockReasonDraft}
                onChange={(e) => setBlockReasonDraft(e.target.value)}
                disabled={!canManage}
              />
            </label>
          ) : null}

          {errorMessage ? <p className="admin-form-error">{errorMessage}</p> : null}

          <form className="admin-form-grid" onSubmit={handleSave}>
            <label>
              Robots.txt URL
              <input
                type="url"
                value={form.robotsTxtUrl}
                onChange={handleChange("robotsTxtUrl")}
                disabled={!canManage}
                placeholder="https://example.com/robots.txt"
              />
            </label>
            <label>
              Robots review status
              <select
                value={form.robotsReviewStatus}
                onChange={handleChange("robotsReviewStatus")}
                disabled={!canManage}
              >
                {VEHICLE_REVIEW_STATUSES.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </label>
            <label className="admin-form-grid__full">
              Robots review notes
              <textarea
                rows={2}
                value={form.robotsReviewNotes}
                onChange={handleChange("robotsReviewNotes")}
                disabled={!canManage}
              />
            </label>

            <label>
              Terms URL
              <input
                type="url"
                value={form.termsUrl}
                onChange={handleChange("termsUrl")}
                disabled={!canManage}
              />
            </label>
            <label>
              Terms review status
              <select
                value={form.termsReviewStatus}
                onChange={handleChange("termsReviewStatus")}
                disabled={!canManage}
              >
                {VEHICLE_REVIEW_STATUSES.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </label>
            <label className="admin-form-grid__full">
              Terms review notes
              <textarea
                rows={2}
                value={form.termsReviewNotes}
                onChange={handleChange("termsReviewNotes")}
                disabled={!canManage}
              />
            </label>

            <label>
              Access review status
              <select
                value={form.accessReviewStatus}
                onChange={handleChange("accessReviewStatus")}
                disabled={!canManage}
              >
                {VEHICLE_REVIEW_STATUSES.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </label>
            <label className="admin-form-grid__full">
              Access review notes
              <textarea
                rows={2}
                value={form.accessReviewNotes}
                onChange={handleChange("accessReviewNotes")}
                disabled={!canManage}
              />
            </label>

            <label className="admin-form-grid__full">
              Rate limit notes
              <textarea
                rows={2}
                value={form.rateLimitNotes}
                onChange={handleChange("rateLimitNotes")}
                disabled={!canManage}
              />
            </label>
            <label className="admin-form-grid__full">
              Data sensitivity notes
              <textarea
                rows={2}
                value={form.dataSensitivityNotes}
                onChange={handleChange("dataSensitivityNotes")}
                disabled={!canManage}
              />
            </label>

            <label className="admin-form-grid__checkbox">
              <input
                type="checkbox"
                checked={form.containsPersonalData}
                onChange={handleChange("containsPersonalData")}
                disabled={!canManage}
              />
              Contains personal data?
            </label>
            <label className="admin-form-grid__checkbox">
              <input
                type="checkbox"
                checked={form.allowedToIngest}
                onChange={handleChange("allowedToIngest")}
                disabled={!canManage}
              />
              Allowed to ingest
            </label>

            <label className="admin-form-grid__full">
              Blocked reason
              <textarea
                rows={2}
                value={form.blockedReason}
                onChange={handleChange("blockedReason")}
                disabled={!canManage}
              />
            </label>
            <label className="admin-form-grid__full">
              Compliance notes
              <textarea
                rows={3}
                value={form.complianceNotes}
                onChange={handleChange("complianceNotes")}
                disabled={!canManage}
              />
            </label>

            {source.reviewedAt ? (
              <p className="muted admin-form-grid__full">
                Last reviewed: {new Date(source.reviewedAt).toLocaleString()}
                {source.reviewedBy ? ` by ${source.reviewedBy}` : ""}
              </p>
            ) : null}

            <section className="admin-form-grid__full admin-vehicle-ingestion-test">
              <h3>Test ingestion (mock only)</h3>
              <p className="muted">
                Runs the M5 mock adapter against local fixtures — no live website scraping.
              </p>
              {canManage ? (
                <button
                  type="button"
                  className="btn btn--secondary"
                  disabled={testRunBusy}
                  onClick={async () => {
                    setTestRunBusy(true);
                    setTestRunResult(null);
                    try {
                      const result = await runVehicleSourceTestIngestion(source.id, {
                        limit: 3,
                        runType: "TEST_RUN",
                      });
                      setTestRunResult(result.run);
                    } catch (error) {
                      setTestRunResult({ error: error.message });
                    } finally {
                      setTestRunBusy(false);
                    }
                  }}
                >
                  {testRunBusy ? "Running…" : "Run test ingestion"}
                </button>
              ) : null}
              {testRunResult ? (
                <dl className="admin-vehicle-ingestion-test__result">
                  {testRunResult.error ? (
                    <dd>{testRunResult.error}</dd>
                  ) : (
                    <>
                      <dt>Status</dt>
                      <dd>{testRunResult.runStatus}</dd>
                      <dt>Started / finished</dt>
                      <dd>
                        {testRunResult.startedAt
                          ? new Date(testRunResult.startedAt).toLocaleString()
                          : "—"}{" "}
                        /{" "}
                        {testRunResult.finishedAt
                          ? new Date(testRunResult.finishedAt).toLocaleString()
                          : "—"}
                      </dd>
                      <dt>Counts</dt>
                      <dd>
                        discovered {testRunResult.recordsDiscovered}, fetched{" "}
                        {testRunResult.recordsFetched}, parsed {testRunResult.recordsParsed},
                        inserted {testRunResult.recordsInserted}
                      </dd>
                      <dt>Errors / warnings</dt>
                      <dd>
                        {testRunResult.errorCount} / {testRunResult.warningCount}
                      </dd>
                      {testRunResult.errorSummary ? (
                        <>
                          <dt>Summary</dt>
                          <dd>{testRunResult.errorSummary}</dd>
                        </>
                      ) : null}
                    </>
                  )}
                </dl>
              ) : null}
            </section>

            {canManage ? (
              <footer className="admin-modal__footer admin-form-grid__full">
                <button type="button" className="btn btn--ghost" onClick={onClose} disabled={saving}>
                  Cancel
                </button>
                <button type="submit" className="btn btn--primary" disabled={saving}>
                  {saving ? "Saving…" : "Save review"}
                </button>
              </footer>
            ) : null}
          </form>
        </div>
      </div>
    </div>
  );
};

export default VehicleSourceReviewPanel;
